import React from "react";
import { Timeline } from "primereact/timeline";

import { ContractTimelineEvent } from "../../../../types/onboarding";
import { delayReasonLabel, humanizeEnum } from "../../../../constants/contractSigning";
import { actorLabel } from "../../../../utils/actors";

interface ContractTimelineProps {
  events: ContractTimelineEvent[];
}

// State transitions are shown in the prospect's "Onboarding progress" (JourneyProgress);
// this feed is only the contract chase activity (reminders / delay reasons), so
// we exclude STATE_TRANSITION to avoid duplicating that timeline.
const EXCLUDED_EVENT_TYPES = new Set(["STATE_TRANSITION"]);

// Human-readable event-type headings (no raw identifiers on screen — FR-021).
const EVENT_LABELS: Record<string, string> = {
  REMINDER_SENT: "Reminder sent",
  DELAY_REASON_LOGGED: "Delay logged",
};

const eventLabel = (type: string): string =>
  EVENT_LABELS[type] ?? humanizeEnum(type);

// Build a short, non-technical detail line from the event's payload.
const detailLine = (event: ContractTimelineEvent): string | null => {
  const d = event.detail;
  if (!d) return null;
  const parts: string[] = [];
  const reason = d.reason_code ?? d.reasonCode;
  if (reason) parts.push(`Reason: ${delayReasonLabel(reason)}`);
  if (d.channel) parts.push(`Via: ${humanizeEnum(String(d.channel))}`);
  if (d.note) parts.push(String(d.note));
  return parts.length ? parts.join(" · ") : null;
};

/**
 * US4: contract chase activity feed — reminders and delay reasons, most-recent
 * first. State transitions are intentionally excluded (they live in the
 * prospect's "Status history"). Purely presentational.
 */
const ContractTimeline: React.FC<ContractTimelineProps> = ({ events }) => {
  const activity = (events ?? []).filter(
    (e) => !EXCLUDED_EVENT_TYPES.has(e.event_type)
  );

  if (!activity.length) {
    return (
      <p className="text-500">
        No reminders or delays logged yet.
      </p>
    );
  }

  const ordered = [...activity].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const content = (e: ContractTimelineEvent) => {
    const detail = detailLine(e);
    const actor = actorLabel(e.actor);
    return (
      <div className="mb-3">
        <div className="font-medium">{eventLabel(e.event_type)}</div>
        <div className="text-500 text-sm">
          {new Date(e.timestamp).toLocaleString()}
          {actor ? ` · ${actor}` : ""}
        </div>
        {detail && <div className="text-sm mt-1">{detail}</div>}
      </div>
    );
  };

  return <Timeline value={ordered} content={content} />;
};

export default ContractTimeline;
